import { createElement, createContext, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { UserRole } from '@/lib/constants'

export interface Profile {
  id: string
  role: UserRole
  full_name: string
  phone: string | null
  avatar_url: string | null
}

interface AuthState {
  session: Session | null
  user: User | null
  profile: Profile | null
  loading: boolean
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // Track whether we've received at least one authoritative session value so
  // that the dual getSession+onAuthStateChange pattern doesn't double-fetch.
  const initializedRef = useRef(false)

  // ── Effect 1: auth state only ───────────────────────────────────────────────
  // Only sets `session`; never calls fetchProfile. Keeping this effect free of
  // profile work means it can never be called from inside Supabase's _useSession
  // lock, which would cause getSession() to return null (anon fallback).
  useEffect(() => {
    let ignore = false

    supabase.auth.getSession().then(({ data }) => {
      if (ignore) return
      if (import.meta.env.DEV) {
        console.log(
          '[KasaGO Auth] getSession →',
          data.session ? `user=${data.session.user.email}` : 'null',
        )
      }
      if (initializedRef.current) return
      initializedRef.current = true
      setSession(data.session)
      if (!data.session) setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (ignore) return
      if (import.meta.env.DEV) {
        console.log(
          '[KasaGO Auth] onAuthStateChange →',
          _event,
          newSession ? `user=${newSession.user.email}` : 'null',
        )
      }
      initializedRef.current = true
      setSession(newSession)
      if (!newSession) {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      ignore = true
      subscription.unsubscribe()
    }
  }, [])

  // ── Effect 2: profile fetch ─────────────────────────────────────────────────
  // Runs OUTSIDE the Supabase auth-lock context so _getAccessToken() →
  // getSession() returns the live session and sets the Authorization header
  // correctly (authenticated role, not anon).
  useEffect(() => {
    if (!session?.user?.id) return

    let ignore = false

    async function run() {
      const userId = session!.user.id

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, role, full_name, phone, avatar_url')
          .eq('id', userId)
          .single()

        if (import.meta.env.DEV) {
          console.log(
            '[KasaGO Auth] profiles SELECT →',
            data ? `found role=${data.role}` : 'null',
            error ? `code=${error.code} msg=${error.message}` : '',
          )
        }

        if (ignore) return

        if (data) {
          setProfile(data)
          return
        }

        // Profile row missing — create it. Fires when handle_new_user trigger
        // didn't run (e.g. user registered before migration 0010 was applied).
        const { data: { user: authUser } } = await supabase.auth.getUser()
        const fullName =
          authUser?.user_metadata?.full_name ??
          authUser?.email?.split('@')[0] ??
          'User'

        const { error: insertError } = await supabase
          .from('profiles')
          .insert({ id: userId, full_name: fullName })

        if (import.meta.env.DEV) {
          console.log(
            '[KasaGO Auth] profile INSERT →',
            insertError ? `code=${insertError.code} msg=${insertError.message}` : 'ok',
          )
        }

        if (ignore) return

        // Re-fetch to get the canonical row (includes DB defaults like role='guest')
        const { data: retry } = await supabase
          .from('profiles')
          .select('id, role, full_name, phone, avatar_url')
          .eq('id', userId)
          .single()

        if (!ignore) setProfile(retry ?? null)
      } catch (err) {
        if (import.meta.env.DEV) {
          console.log('[KasaGO Auth] fetchProfile caught error:', err)
        }
        if (!ignore) setProfile(null)
      } finally {
        if (!ignore) setLoading(false)
      }
    }

    run()
    return () => { ignore = true }
  }, [session?.user?.id])

  async function signOut() {
    await supabase.auth.signOut()
  }

  async function refreshProfile() {
    if (!session?.user?.id) return
    const userId = session.user.id

    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, full_name, phone, avatar_url')
      .eq('id', userId)
      .single()

    if (import.meta.env.DEV) {
      console.log(
        '[KasaGO Auth] refreshProfile →',
        data ? `role=${data.role}` : 'null',
        error?.message ?? '',
      )
    }

    if (data) setProfile(data)
  }

  return createElement(AuthContext.Provider, {
    value: {
      session,
      user: session?.user ?? null,
      profile,
      loading,
      signOut,
      refreshProfile,
    },
    children,
  })
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
